package com.xtream.composeapp.presentation.ui.search

import com.xtream.composeapp.presentation.ui.search.SearchDefaults.FULL_YEAR_RANGE_MOVIE
import com.xtream.composeapp.presentation.ui.search.SearchDefaults.FULL_YEAR_RANGE_SERIES
import com.xtream.data_kmp.db.dao.LiveSortBy
import com.xtream.data_kmp.db.dao.MovieSortBy
import com.xtream.data_kmp.db.dao.SeriesSortBy
import com.xtream.data_kmp.dto.CategoryBase
import com.xtream.data_kmp.dto.LiveCategory
import com.xtream.data_kmp.dto.LiveStream
import com.xtream.data_kmp.dto.SeriesCategory
import com.xtream.data_kmp.dto.VodCategory
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atStartOfDayIn
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Clock

object TimeProvider {
    val currentYear: Int
        get() = Clock.System.now()
            .toLocalDateTime(TimeZone.currentSystemDefault())
            .year
}
fun IntRange.toUnixRange(): IntRange {
    val from = LocalDate(first, 1, 1)
        .atStartOfDayIn(TimeZone.UTC).epochSeconds.toInt()
    val to = LocalDate(last, 12, 31)
        .atStartOfDayIn(TimeZone.UTC).epochSeconds.toInt()
    return from..to
}
/* =========================
   BASE SEARCH STATE
========================= */
interface  BaseSearchState {
     val isLoading: Boolean
    val error: String?
    val selectedCategories: List<CategoryBase>?
}
enum class TvStreamType   { ALL, LIVE, CATCHUP }

data class SearchLiveState(
    override val isLoading: Boolean=false,
    override val error: String?=null,

    override val selectedCategories: List<LiveCategory>? = null,
    val sortBy: LiveSortBy = LiveSortBy.LAST_ADDED,
    val type:TvStreamType= TvStreamType.ALL

):BaseSearchState

object SearchDefaults {
    val FULL_YEAR_RANGE_MOVIE= IntRange(2015, TimeProvider.currentYear)
    val FULL_YEAR_RANGE_SERIES= IntRange(1970, TimeProvider.currentYear)
}
data class SearchMovieState(
    override val isLoading: Boolean=false,
    override val error: String?=null,
    val sortBy: MovieSortBy =MovieSortBy.LAST_ADDED,
    val range: IntRange= FULL_YEAR_RANGE_MOVIE,
    override val selectedCategories: List<VodCategory>? = null
):BaseSearchState

data class SearchSeriesState(
    override val isLoading: Boolean = false,
    override val error: String? = null,
    val sortBy: SeriesSortBy = SeriesSortBy.LAST_ADDED,
    val range: IntRange= FULL_YEAR_RANGE_SERIES,
    override val selectedCategories: List<SeriesCategory>? = null
):BaseSearchState



data class SearchUiConfigState(
    val query: String?=null,
    val liveTvConfig: SearchLiveState=SearchLiveState(),
    val movieConfig: SearchMovieState = SearchMovieState(),
    val seriesConfig: SearchSeriesState=SearchSeriesState()
)




data class BaseSearchUiState(
    val query: String = "",
    val isLoading: Boolean = false,
    val error: String? = null,
    val selectedCategoryIds: List<Int> = emptyList()
)

/* =========================
   LIVE
========================= */


data class LiveFilterUiState(
    val base: BaseSearchUiState = BaseSearchUiState(),
    val sort: LiveSortBy = LiveSortBy.DEFAULT,
    val streams: List<LiveStream> = emptyList()
)

/* =========================
   MOVIES (VOD)
========================= */


enum class MovieDuration {
    ANY,
    SHORT,
    MEDIUM,
    LONG
}

data class VodFilterUiState(
    val base: BaseSearchUiState = BaseSearchUiState(),
    val sort: MovieSortBy = MovieSortBy.LAST_ADDED,
    val yearFrom: Int = 1970,
    val yearTo: Int = TimeProvider.currentYear,
    val genres: List<String> = emptyList(),
    val minRating: Float? = null,
    val duration: MovieDuration = MovieDuration.ANY
)

/* =========================
   SERIES
========================= */


enum class SeriesStatus {
    ALL,
    ONGOING,
    ENDED
}

enum class SeriesSeasons {
    ANY,
    ONE,
    TWO_TO_FOUR,
    FIVE_PLUS
}

data class SeriesFilterUiState(
    val base: BaseSearchUiState = BaseSearchUiState(),
    val sort: SeriesSortBy = SeriesSortBy.LAST_ADDED,
    val yearFrom: Int = 1970,
    val yearTo: Int = TimeProvider.currentYear,
    val genres: List<String> = emptyList(),
    val status: SeriesStatus = SeriesStatus.ALL,
    val seasons: List<SeriesSeasons> = emptyList(),
    val minRating: Float? = null
)

/* =========================
   OPTIONAL GLOBAL FILTER STATE
========================= */
data class FilterState(
    val live: LiveFilterUiState = LiveFilterUiState(),
    val movie: VodFilterUiState = VodFilterUiState(),
    val series: SeriesFilterUiState = SeriesFilterUiState()
)

enum class FilterTab { TV, MOVIES, SERIES }


fun LiveSortBy.label() = when(this) {

    LiveSortBy.NAME_ASC   -> "Name A–Z"
    LiveSortBy.NAME_DESC  -> "Name Z–A"
    LiveSortBy.CATEGORY   -> "Category"
    LiveSortBy.LAST_ADDED -> "Last added"
    LiveSortBy.DEFAULT -> "Default"
}
fun TvStreamType.label() = when(this) {
    TvStreamType.ALL     -> "All"
    TvStreamType.LIVE    -> "Live"
    TvStreamType.CATCHUP -> "Catchup"
}
fun MovieSortBy.label() = when(this) {
    MovieSortBy.NAME_ASC -> "Name A–Z"
    MovieSortBy.NAME_DESC  -> "Name Z–A"
    MovieSortBy.LAST_ADDED  -> "Last added"
    MovieSortBy.RATING  -> "Rating"
    MovieSortBy.DEFAULT -> "Default"

}
fun MovieDuration.label() = when(this) {
    MovieDuration.ANY    -> "Any"
    MovieDuration.SHORT  -> "Under 1.5h"
    MovieDuration.MEDIUM -> "1.5h – 2.5h"
    MovieDuration.LONG   -> "Over 2.5h"
}

fun SeriesSeasons.label() = when(this) {
    SeriesSeasons.ANY        -> "Any"
    SeriesSeasons.ONE        -> "1 season"
    SeriesSeasons.TWO_TO_FOUR -> "2–4 seasons"
    SeriesSeasons.FIVE_PLUS  -> "5+ seasons"
}
fun SeriesSortBy.label() = when(this) {
    SeriesSortBy.NAME_ASC      -> "Name A–Z"
    SeriesSortBy.NAME_DESC     -> "Name Z–A"
    SeriesSortBy.RATING        -> "Rating"
    SeriesSortBy.NEWEST        -> "Newest first"
    SeriesSortBy.LAST_ADDED    -> "Last added"
    SeriesSortBy.DEFAULT -> "Default"


}